import React, { useEffect, useState } from 'react';
import useBoardContentWidgetData from '../../../hooks/useBoardContentWidgetData';
import useBoardContentWidget from '../../../hooks/useBoardContentWidget';
import { useInternationalization } from '@progress/kendo-react-intl';
import useTranslate from '../../../../../common/hooks/useTranslate';
import { POPUP } from '../../../../../redux/types';
import { useDispatch } from 'react-redux';

import { IBoardNote } from '../../../types/IBoardNote';
import { IWidgetData } from '../../../types/IWidgetData';
import { WidgetProps } from '../../../types/IWidgetProps';
import { ICurrency } from '../../../types/ICurrency';
import { IMeasureItem } from './types/IMeasureItem';
import { IMapSetup } from './types/IMapSetup';
import { IMeasureLayer } from './types/IMeasureLayer';
import { IMeasureLayerData } from './types/IMeasureLayerData';

import { DropDownList } from '@progress/kendo-react-dropdowns';
import { Map, MapBubbleLayer, MapBubbleLayerTooltip, MapLayers, MapTileLayer } from '@progress/kendo-react-map';
import WidgetWrapper from '../widgetWrapper';
import ContentLoading from '../../../../ui/loaders/contentLoading/contentLoading';

import './mapWidget.scss';

const MapWidget: React.FC<WidgetProps> = ({
                                            widget,
                                            selectedObject,
                                            selectedPeriod,
                                            selectedCurrency,
                                            boardData,
                                            scenarios,
                                            customisation,
                                            moveWidget,
                                            content,
                                            removeWidget,
                                            notes,
                                            handleLink,
                                          }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [lat, setLat] = useState<number>(35);
  const [long, setLong] = useState<number>(-10);
  const [zoom, setZoom] = useState<number>(3);
  const [localCurrency, setLocalCurrency] = useState<ICurrency>();
  const [mapNotes, setMapNotes] = useState<IBoardNote[]>([]);
  const [selectData, setSelectData] = useState<IMeasureItem[]>([]);
  const [widgetCurrencies, setWidgetCurrencies] = useState<ICurrency[]>([]);
  const [mapSetup, setMapSetup] = useState<IMapSetup>();
  const [measureLayers, setMeasureLayers] = useState<IMeasureLayer[]>([]);
  const [selectedMeasure, setSelectedMeasure] = useState<IMeasureItem>();
  const [values, setValues] = useState<IMeasureLayerData[]>([]);

  const { defineNodes, fetchNodeData, generateCurrencies } = useBoardContentWidgetData();
  const { widgetSetup } = useBoardContentWidget();
  const intl = useInternationalization();
  const dispatch = useDispatch();
  const { t } = useTranslate();

  const center = [lat, long];
  const tileSubdomains = ['a', 'b', 'c'];
  const tileUrl = (e) => `https://${e.subdomain}.tile.openstreetmap.org/${e.zoom}/${e.x}/${e.y}.png`;
  const attribution = '&copy; <a href="https://osm.org/copyright">OpenStreetMap contributors</a>';

  const bubbleStyle = {
    fill: {
      color: '#00f',
      opacity: 0.4,
    },
  };

  const renderBubbleTooltip = (props) => (
    <div>
      <span>{props.dataItem.name}</span>
      <br />
      <span>
        {props.dataItem.measureName}: {intl.toString(props.dataItem.value, `n${props.dataItem.scale}`)}
      </span>
    </div>
  );

  const preventFromZoom = (e) => {
    if (e.originalEvent && e.originalEvent.type && e.originalEvent.wheelDelta) {
      e.preventDefault();
    }
  };

  const filterNotes = (notes: IBoardNote[]) => {
    let mapNotes: IBoardNote[] = [];

    for (let i = 0; i < notes.length; i++) {
      const measures = notes[i] && notes[i].measures;
      if (measures) {
        for (let j = 0; j < measures.length; j++) {
          const measureId = measures[j].measureId;
          mapSetup?.measures?.map((m) => {
            if (m.measureId === measureId && !mapNotes.includes(notes[i]))
              mapNotes.push(notes[i]);
          });
        }
      }
    }

    setMapNotes(mapNotes);
  };

  const defineLayers = (setup: IMapSetup) => {
    let dropdownData: IMeasureItem[] = [];
    let measureLayers: IMeasureLayer[] = [];

    setup?.measures?.forEach((measure) => {
      let boardMeasure = boardData?.measures?.find((item) => {
        return item.id === measure.measureId;
      });

      if (boardMeasure) {
        measureLayers.push({
          measureId: measure.measureId,
          name: boardMeasure.name,
          amountsIn: measure.amountsIn,
          scale: measure.scale,
          viewCashflows: measure.viewCashflows,
          data: [],
        });

        dropdownData.push(
          {
            measureId: measure.measureId,
            name: boardMeasure.name,
          },
        );
      }
    });

    setMeasureLayers(measureLayers);
    setSelectData(dropdownData);

    if (dropdownData.length > 0)
      setSelectedMeasure(dropdownData[0]);
  };

  const clearLayerData = () => {
    setMeasureLayers(measureLayers.map((l) => ({ ...l, data: [] })));
  };

  const getLayer = (measureId: number): IMeasureLayer | undefined => {
    return measureLayers.find((layer) => {
      return layer.measureId === measureId;
    });
  };

  const onSelectChange = (e) => {
    setSelectedMeasure(e.value);

    let layer = measureLayers.find((l) => {
      return l.measureId === e.value.measureId;
    });

    if (layer) {
      setValues(layer.data);
    }
  };

  const assembleLayerData = (data: IWidgetData[]) => {
    clearLayerData();

    let nodes = selectedObject?.nodes;

    data.forEach((value) => {
      let layer = getLayer(value.measureId);

      if (layer) {
        let node = nodes?.find((item) => {
          return item.id === value.nodeId;
        });

        if (node)
          layer.data.push({
            name: node.name,
            measureName: layer.name,
            scale: layer.scale,
            location: [node.latitude, node.longitude],
            value: layer.amountsIn ? (value.amount / layer.amountsIn) : 0,
            viewCashflows: layer.viewCashflows,
            nodeId: value.nodeId,
            valueId: value.id,
            scenarioId: value.scenarioId,
            measureId: value.measureId,
          });
      }
    });
  };

  const handleLocalCurrencyChange = (e) => {
    setLocalCurrency(e.value);
  };

  useEffect(() => {
    let wSetup = widgetSetup(widget);
    if (wSetup) {
      setMapSetup(wSetup);
      setLat(parseFloat(wSetup.latitude));
      setLong(parseFloat(wSetup.longitude));
      setZoom(parseInt(wSetup.zoom));
      defineLayers(wSetup);
    }
  }, []);

  useEffect(() => {
    if (selectedObject && selectedPeriod && localCurrency && selectedMeasure) {
      const { nodes } = defineNodes(widget, selectedObject);

      if (scenarios) {
        setWidgetCurrencies(generateCurrencies(widget, nodes, scenarios));
      }

      fetchNodeData(widget, nodes, boardData?.measures || [], selectedPeriod, localCurrency.currency || '', false)
        .then((data) => {
          assembleLayerData(data);

          let layer = getLayer(selectedMeasure.measureId || 0);
          if (layer) setValues(layer.data);

          setLoading(false);
        })
        .catch((err) => {
          dispatch({ type: POPUP.SET_SERVER_ERROR, payload: err.message });
        });
    }
  }, [selectedObject, selectedPeriod, localCurrency, selectedMeasure]);

  useEffect(() => {
    if (notes) {
      filterNotes(notes);
    }
  }, [notes, widget]);

  useEffect(() => {
    setLocalCurrency(selectedCurrency);
  }, [selectedCurrency]);

  return (
    window.innerWidth < 960 && widget?.setup?.hideOnMobile ? null :
      <WidgetWrapper widget={widget} localCurrency={localCurrency} widgetCurrencies={widgetCurrencies}
                     handleLocalCurrencyChange={handleLocalCurrencyChange} notes={mapNotes} moveWidget={moveWidget}
                     removeWidget={removeWidget} handleLink={handleLink} content={content}
                     selectedObject={selectedObject} customisation={customisation}>
        {loading ? <ContentLoading /> : (
          <div className='map-widget-wrap'>
            <div className='view-by-dropdown-wrapper'>
              <span className='view-by-dropdown-label'>{t('WIDGET.VIEW_BY')}</span>
              <DropDownList data={selectData} defaultValue={selectData[0]} onChange={onSelectChange}
                            textField='name' dataItemKey='measureId' />
            </div>
            <Map className='map-container' center={center} zoom={zoom} onZoomStart={preventFromZoom}>
              <MapLayers>
                <MapTileLayer urlTemplate={tileUrl} subdomains={tileSubdomains} attribution={attribution} />
                <MapBubbleLayer data={values} locationField='location' valueField='value'
                                style={bubbleStyle} zIndex={1}>
                  <MapBubbleLayerTooltip render={renderBubbleTooltip} />
                </MapBubbleLayer>
              </MapLayers>
            </Map>
          </div>
        )}
      </WidgetWrapper>
  );
};

export default MapWidget;